import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { authApi, ApiError, type WhoAmIResponse } from "./api";
import { AuthContext, type AuthContextValue, type AuthState, type PersistedSession } from "./authContext";

const STORAGE_KEY = "liando.session";

function readPersisted(): PersistedSession | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<PersistedSession>;
    if (
      typeof parsed.token === "string" &&
      typeof parsed.user_id === "string" &&
      typeof parsed.username === "string" &&
      (parsed.role === "admin" || parsed.role === "user")
    ) {
      return parsed as PersistedSession;
    }
  } catch {
    // corrupt blob — fall through and clear
  }
  localStorage.removeItem(STORAGE_KEY);
  return null;
}

function writePersisted(session: PersistedSession | null): void {
  if (session) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
  } else {
    localStorage.removeItem(STORAGE_KEY);
  }
}

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [state, setState] = useState<AuthState>({ status: "loading" });

  useEffect(() => {
    let cancelled = false;
    const persisted = readPersisted();
    if (!persisted) {
      setState({ status: "anonymous" });
      return;
    }
    authApi
      .whoami(persisted.token)
      .then((me: WhoAmIResponse) => {
        if (cancelled) return;
        if (me.disabled) {
          writePersisted(null);
          setState({ status: "anonymous" });
          return;
        }
        setState({
          status: "authenticated",
          session: { ...persisted, username: me.username, role: me.role },
        });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        // token expired / revoked → drop it
        if (err instanceof ApiError && err.status === 401) {
          writePersisted(null);
        }
        setState({ status: "anonymous" });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(async (username: string, password: string) => {
    const res = await authApi.login(username, password);
    const session: PersistedSession = {
      token: res.token,
      user_id: res.user_id,
      username: res.username,
      role: res.role,
    };
    writePersisted(session);
    setState({ status: "authenticated", session });
  }, []);

  const loginWithSession = useCallback((session: PersistedSession) => {
    writePersisted(session);
    setState({ status: "authenticated", session });
  }, []);

  const logout = useCallback(async () => {
    const current = state.status === "authenticated" ? state.session : null;
    if (current) {
      try {
        await authApi.logout(current.token);
      } catch {
        // logout endpoint is idempotent; ignore failures
      }
    }
    writePersisted(null);
    setState({ status: "anonymous" });
  }, [state]);

  const value = useMemo<AuthContextValue>(
    () => ({ state, login, loginWithSession, logout }),
    [state, login, loginWithSession, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export type { PersistedSession } from "./authContext";
